import React from 'react'
import PropTypes from 'prop-types'

// Form dependencies
import {zodResolver} from '@hookform/resolvers/zod'
import {useForm} from 'react-hook-form'
import {z} from 'zod'

// Helpers
import {cn} from '@/lib/utils'

// Components
import {Button} from '@/components/ui/button'
import {Form, FormControl, FormField, FormItem, FormLabel, FormMessage} from '@/components/ui/form'
import {RadioGroup, RadioGroupItem} from '@/components/ui/radio-group'

const QuizForm = ({module, requestInProgress, submitQuiz, updatedScrollPos}) => {
    const questions = module.quiz.questions
    const schemaObj = {}

    questions.forEach((question, questionIndex) => {
        const values = Array.from(question.answers, (_, index) => index.toString())

        schemaObj[`question-${questionIndex}`] = z.enum(values, {
            required_error: 'You need to select an answer'
        })
    })

    const FormSchema = z.object(schemaObj)
    const form = useForm({resolver: zodResolver(FormSchema)})

    return (
        <Form {...form} data-testid="quizForm">
            <form onSubmit={form.handleSubmit(submitQuiz)}>
                {questions.map((question, questionIndex) => (
                    <FormField
                        key={`question-${questionIndex}`}
                        control={form.control}
                        name={`question-${questionIndex}`}
                        render={({field}) => (
                            <FormItem
                                className={cn('space-y-5', questionIndex > 0 && 'mt-[2.6875rem]')}
                                data-testid="quizForm-FormItem"
                            >
                                <FormLabel
                                    className="text-sm leading-normal lg:text-base font-serif font-medium text-black"
                                    data-testid="quizForm-FormLabel"
                                >
                                    {question.question}
                                </FormLabel>
                                <FormControl data-testid="quizForm-FormControl">
                                    <RadioGroup
                                        onValueChange={field.onChange}
                                        defaultValue={field.value}
                                        className="flex flex-col space-y-[1.1875rem]"
                                        data-testid="quizForm-RadioGroup"
                                    >
                                        {question.answers.map((answer, answerIndex) => (
                                            <FormItem
                                                key={`question-${questionIndex}-answer-${answerIndex}`}
                                                className="flex items-center space-x-4 space-y-0"
                                                data-testid="quizForm-FormItem"
                                            >
                                                <FormControl data-testid="quizForm-FormControl">
                                                    <RadioGroupItem
                                                        value={answerIndex.toString()}
                                                        // Assign unique id
                                                        id={`question-${questionIndex}-answer-${answerIndex}`}
                                                        data-testid="quizForm-RadioGroupItem"
                                                    />
                                                </FormControl>
                                                <FormLabel
                                                    htmlFor={`question-${questionIndex}-answer-${answerIndex}`}
                                                    className="font-light text-emphasis text-sm leading-normal lg:text-base"
                                                    data-testid="quizForm-FormLabel"
                                                >
                                                    {answer}
                                                </FormLabel>
                                            </FormItem>
                                        ))}
                                    </RadioGroup>
                                </FormControl>
                                <FormMessage data-testid="quizForm-FormMessage" />
                            </FormItem>
                        )}
                        data-testid="quizForm-FormField"
                    />
                ))}
                <div className="flex mt-[3.25rem]">
                    <Button
                        ctaType="button"
                        variant="primary"
                        className="w-full lg:w-[14.1875rem] lg:ml-auto"
                        type="submit"
                        disabled={requestInProgress}
                        onClick={updatedScrollPos}
                        data-testid="quizForm-Button"
                    >
                        Submit
                    </Button>
                </div>
            </form>
        </Form>
    )
}

QuizForm.propTypes = {
    module: PropTypes.object.isRequired,
    requestInProgress: PropTypes.bool.isRequired,
    submitQuiz: PropTypes.func.isRequired,
    updatedScrollPos: PropTypes.func.isRequired
}

export default QuizForm
